import type { FeatureConfig } from '@features/userManagement/types';

/**
 * `feature` is optional because Hasura makes it so. `dealer-admin` may select
 * every grant row belonging to a user it can see, but it may only select a
 * `features` row whose `application` is `portal`, so a grant pointing anywhere
 * else comes back as a row with a null feature. That is a permission result,
 * not corrupt data, and it means the same thing as no grant: no access.
 */
export type FeatureGrant = {
  feature?: { name?: string | null; config?: unknown } | null;
} | null | undefined;

const readableFeature = (grant: FeatureGrant) => {
  const name = grant?.feature?.name;
  return name ? { name, config: grant?.feature?.config } : null;
};

export const grantedFeatureNames = (grants?: readonly FeatureGrant[] | null): string[] =>
  (grants ?? []).reduce<string[]>((names, grant) => {
    const feature = readableFeature(grant);
    if (feature) names.push(feature.name);
    return names;
  }, []);

export const grantedFeatureConfigs = (
  grants?: readonly FeatureGrant[] | null
): Record<string, FeatureConfig | null> =>
  (grants ?? []).reduce<Record<string, FeatureConfig | null>>((configs, grant) => {
    const feature = readableFeature(grant);
    if (feature) configs[feature.name] = (feature.config as FeatureConfig) ?? null;
    return configs;
  }, {});
